import { Injectable } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { AssetListSharedState, AssetListStateService } from './asset-list-state.service';
import { OverlayService } from '../../../services/overlayService';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { AssetModelComponent } from '../../asset-model/asset-model.component';
import { MoveToModalComponent } from '../../move-to-modal/move-to-modal.component';
import { Asset, SubAsset } from '../models/asset.model';

@Injectable({
  providedIn: 'root',
})
export class AssetListDragDropService {
  constructor(
    private readonly stateService: AssetListStateService,
    private readonly modalService: NgbModal,
    private readonly overlayService: OverlayService,
    private readonly translatePipe: TranslatePipe
  ) {}

  public onDragStart(card: Asset): void {
    this.stateService.updateState({ draggedCard: card });
  }

  public onDragOver(event: DragEvent, targetCardId: string): void {
    event.preventDefault();
    this.stateService.updateState({ targetCardId });
  }

  public onDragLeave(): void {
    this.stateService.updateState({ targetCardId: null });
  }

  public onDragEnd(): void {
    this.stateService.updateState({ draggedCard: null, targetCardId: null });
  }

  public onMoveToModalOpen(
    source: Asset,
    bundle: Asset[],
    campaignId: string,
    onDropCallback: (target: Asset) => void
  ): void {
    this.stateService.updateState({ draggedCard: source });
    const assets: Asset[] = bundle.filter((a: Asset) => a.id !== source.id && !a.hidden);
    const moveToModal: NgbModalRef = this.modalService.open(MoveToModalComponent, {
      size: 'lg',
      keyboard: false,
      backdrop: 'static',
      modalDialogClass: 'responsive-modal',
    });
    moveToModal.componentInstance.source = source;
    moveToModal.componentInstance.assets = assets;
    moveToModal.componentInstance.campaignId = campaignId;
    moveToModal.result.then((target: Asset) => {
      if (target?.id) {
        onDropCallback(target);
      } else {
        this.stateService.updateState({ draggedCard: null });
      }
    });
  }

  public onDrop(
    targetCard: Asset,
    bundle: Asset[],
    doMoveAssetCallback: (dragAsset: Asset, dropAsset: Asset) => void,
    showConfirmDialog: boolean = true
  ): void {
    if (!targetCard?.id) return;

    const state: AssetListSharedState = this.stateService.currentState;
    const dragAsset: Asset = state.draggedCard;
    const dropAsset: Asset = targetCard;

    if (!dragAsset || !dropAsset) {
      this.stateService.updateState({ draggedCard: null, targetCardId: null });
      return;
    }

    if (dragAsset.id === dropAsset.id) {
      this.stateService.updateState({ draggedCard: null, targetCardId: null });
      return;
    }

    if (dragAsset && state.affectedAssets[dragAsset.id]) {
      this.overlayService.showError('This Master asset is already assigned to a project and cannot be moved!');
      this.stateService.updateState({ draggedCard: null, targetCardId: null });
      return;
    }

    if (showConfirmDialog) {
      const modalRef: NgbModalRef = this.modalService.open(AssetModelComponent, {
        backdrop: 'static',
        keyboard: false,
      });

      modalRef.componentInstance.title = this.translatePipe.transform('asset_mergeAsset') || 'Merge Asset';
      modalRef.componentInstance.description = `You're about to convert <b>"${dragAsset.detail || dragAsset.title}"</b> into a Technical Variant of  <b>"${dropAsset.detail || dropAsset.title}"</b>.<br><br> This action will change it's status and link it under the selected Master.`;

      modalRef.result
        .then((confirm: boolean) => {
          if (!confirm) {
            this.stateService.updateState({ draggedCard: null, targetCardId: null });
            return;
          }

          doMoveAssetCallback(dragAsset, dropAsset);
        })
        .catch(() => {
          // Modal dismissed
        });
    } else {
      doMoveAssetCallback(dragAsset, dropAsset);
    }
  }

  public doMoveAsset(
    dragAsset: Asset,
    dropAsset: Asset,
    bundle: Asset[],
    dragAndDropCallback?: (data: { dragAssetId: string; dropAssetId: string }) => void,
    bundleChangeCallback?: (bundle: Asset[]) => void
  ): Asset[] {
    const dragSubAsset: SubAsset[] = [
      ...(dragAsset?.subAsset || []),
      {
        id: dragAsset.id,
        funnel: dropAsset.funnel,
        type: dragAsset.type,
        detail: dragAsset.detail,
        focus: dragAsset.focus,
        categories: dragAsset.categories,
        bets: dragAsset.bets,
        length: dragAsset.length,
        format: dragAsset.format,
        assetType: 'TECHNICAL_VARIANT',
        initial: dropAsset.initial,
      },
    ];

    let newBundle: Asset[] = bundle.filter((asset: Asset) => asset.id !== dragAsset?.id);

    newBundle = newBundle.map((asset: Asset) => {
      if (asset.id === dropAsset.id) {
        return {
          ...asset,
          subAsset: [...(Array.isArray(asset.subAsset) ? asset.subAsset : []), ...dragSubAsset],
        };
      }
      return asset;
    });

    if (dragAndDropCallback) {
      dragAndDropCallback({
        dragAssetId: dragAsset.id,
        dropAssetId: dropAsset.id,
      });
    }

    this.overlayService.showSuccess('Asset successfully moved as a Technical Variant!');
    this.stateService.updateState({ draggedCard: null, targetCardId: null });

    if (bundleChangeCallback) {
      bundleChangeCallback(newBundle);
    }

    return newBundle;
  }
}
